import React, { useRef } from 'react';

const TOUCH_CLICK_SUPPRESSION_MS = 750;

type Props = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'onPointerDown' | 'onPointerUp' | 'onPointerCancel' | 'type'
> & {
  onPress: () => void;
  type?: 'button' | 'submit';
  touchAction?: 'pan-y' | 'none'; // use it where nothing scrolls (e.g. the kiosk keypad).
};

/**
 * A button whose touch press survives finger drift.
 *
 * iPad Safari normally turns a moving touch into a page pan and cancels the
 * click. `touch-pan-y` still allows vertical scrolling (a scroll gesture
 * cancels the press, which is correct for long option lists), while pointer
 * capture keeps a non-scrolling press assigned to this button until the
 * patient lifts their finger, even if they drift outside its visual bounds.
 * Mouse and keyboard activation continue to use the native click path.
 */
export const TolerantTouchButton = React.forwardRef<HTMLButtonElement, Props>(
  ({ onPress, disabled, className, type = 'button', touchAction = 'pan-y', ...props }, ref) => {
    const touchPointerIdRef = useRef<number | null>(null);
    const suppressClicksUntilRef = useRef(0);

    const releasePointer = (button: HTMLButtonElement, pointerId: number) => {
      if (button.hasPointerCapture(pointerId)) {
        button.releasePointerCapture(pointerId);
      }
    };

    return (
      <button
        ref={ref}
        {...props}
        type={type}
        disabled={disabled}
        className={`${touchAction === 'none' ? 'touch-none' : 'touch-pan-y'} select-none ${className ?? ''}`}
        onPointerDown={event => {
          if (disabled || event.pointerType !== 'touch' || !event.isPrimary) return;

          touchPointerIdRef.current = event.pointerId;
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerUp={event => {
          if (event.pointerId !== touchPointerIdRef.current) return;

          touchPointerIdRef.current = null;
          releasePointer(event.currentTarget, event.pointerId);
          suppressClicksUntilRef.current = Date.now() + TOUCH_CLICK_SUPPRESSION_MS;
          onPress();

          if (type === 'submit') {
            event.currentTarget.form?.requestSubmit(event.currentTarget);
          }
        }}
        onPointerCancel={event => {
          if (event.pointerId !== touchPointerIdRef.current) return;

          touchPointerIdRef.current = null;
          releasePointer(event.currentTarget, event.pointerId);
        }}
        onClick={event => {
          // Older iPads may wait hundreds of milliseconds after pointerup
          // before emitting the synthetic click. `detail === 0` identifies
          // keyboard/programmatic activation, which must remain immediate.
          if (event.detail > 0 && Date.now() <= suppressClicksUntilRef.current) {
            suppressClicksUntilRef.current = 0;
            event.preventDefault();

            return;
          }

          onPress();
        }}
      />
    );
  }
);

TolerantTouchButton.displayName = 'TolerantTouchButton';
